import { useState, useEffect, DependencyList } from 'react';
import get from 'lodash/get';


export type DoNotSet = Symbol;
export type ResourceLoadFn<T> = (abortController: AbortController) => Promise<T | DoNotSet>;

const DONOTSET: DoNotSet = Symbol("DoNotSet");

function useResourceLoader<T>(
  resourceFn: ResourceLoadFn<T>,
  dependencies: DependencyList,
  initialValue?: undefined,
  resourcePath?: string
): [
  T | undefined,
  boolean | undefined,
  Error | undefined,
  React.Dispatch<React.SetStateAction<T | undefined>>
];


function useResourceLoader<T>(
  resourceFn: ResourceLoadFn<T>,
  dependencies: DependencyList,
  initialValue: T,
  resourcePath?: string
): [
  T,
  boolean | undefined,
  Error | undefined,
  React.Dispatch<React.SetStateAction<T>>
];

/** Hook for loading a resource.
 * Loads data as an effect whenever the dependencies change.
 *
 * @param {Function} dataLoadFn - Synchronous or asynchronous function returning the data.
 * @param {DependencyList} dependencies - List of dependencies for the effect. The function is called whenever an item changes in this list.
 * @param {T} initialValue - Initial value of the data.
 *
 * @returns {Array} - An array containing the loaded data, loading state, error state, and a function to set data.
 */
function useResourceLoader<T>(
  resourceFn: ResourceLoadFn<T>,
  dependencies: DependencyList,
  initialValue?: T,
  resourcePath?: string
): [
  T | undefined,
  boolean | undefined,
  Error | undefined,
  React.Dispatch<React.SetStateAction<T | undefined>>
] {
  const [resource, setResource] = useState<T | undefined>(initialValue);
  const [loaderState, setLoaderState] = useState<
    { loading: boolean; error?: Error; deps: DependencyList } | undefined
  >()
  const { error } = loaderState || {}
  // On the render where dependencies change, the effect below hasn't run yet, so loaderState
  // still describes the previous load. Report loading for that render instead of a stale
  // `false`, otherwise callers flash their "no data" state for a frame before reloading.
  const loading = loaderState ? loaderState.loading || !sameDependencies(loaderState.deps, dependencies) : undefined;

  useEffect(() => {
    setLoaderState({ loading: true, deps: dependencies });
    const abortController = new AbortController();
    const flags = { canceled: false, abortController };

    Promise.resolve()
      .then(() => resourceFn(abortController))
      .then(newResource => {
        if (flags.canceled) return;

        if (newResource !== DONOTSET && newResource !== resource) {
          setResource(resourcePath ? get(newResource, resourcePath) : newResource);
        }

        setLoaderState({ loading: false, deps: dependencies });
      })
      .catch(error => {
        if (flags.canceled) return;

        setLoaderState({
          loading: false,
          error,
          deps: dependencies
        })
      });

    return () => {
      flags.canceled = true;
      flags.abortController.abort();
    };
  }, dependencies);

  return [resource, loading, error, setResource];
}

function sameDependencies(previous: DependencyList, current: DependencyList): boolean {
  return previous.length === current.length && previous.every((dep, index) => Object.is(dep, current[index]));
}

useResourceLoader.DoNotSet = DONOTSET;

export default useResourceLoader;
